import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { getDefaultTemplateRules, getDefaultSectionRules } from '@/config/templateDefaults';
import { TemplateRulesService } from '@/services/templateRules';
import type { TemplateType, ContentType, WebsiteTemplateRule, WebsiteSectionRule } from '@/types/templateRules';
import { getTemplateIcon } from '@/lib/templateIcons';
import { Link } from 'react-router-dom';

interface TemplateTypeInfo {
  id: string;
  name: string;
  icon: string;
}

interface TemplateRulesEditorProps {
  websiteId: string;
  onSave?: () => void;
}

export const TemplateRulesEditor: React.FC<TemplateRulesEditorProps> = ({
  websiteId,
  onSave
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [templateTypes, setTemplateTypes] = useState<TemplateTypeInfo[]>([]);
  const [templateRules, setTemplateRules] = useState<Partial<Record<`${TemplateType}:${ContentType}`, WebsiteTemplateRule>>>({});
  const [sectionRules, setSectionRules] = useState<Partial<Record<`${TemplateType}:${ContentType}`, WebsiteSectionRule[]>>>({});
  const [defaultTemplateRules, setDefaultTemplateRules] = useState<Record<`${TemplateType}:${ContentType}`, Omit<WebsiteTemplateRule, 'id' | 'website_id' | 'created_at' | 'updated_at'>>>({});
  const [defaultSectionRules, setDefaultSectionRules] = useState<Record<`${TemplateType}:${ContentType}`, Omit<WebsiteSectionRule, 'id' | 'website_id' | 'created_at' | 'updated_at'>[]>>({});
  const [sectionTypes, setSectionTypes] = useState<Array<{
    id: string;
    name: string;
    description: string | null;
  }>>([]);

  // Load template types and default rules from database
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [templateTypesData, sectionTypesData, defaultTemplateRulesData, defaultSectionRulesData] = await Promise.all([
          supabase
            .from('template_types')
            .select('*')
            .order('name'),
          supabase
            .from('section_types')
            .select('*')
            .order('name'),
          getDefaultTemplateRules(),
          getDefaultSectionRules()
        ]);

        if (templateTypesData.error) throw templateTypesData.error;
        if (sectionTypesData.error) throw sectionTypesData.error;
        
        setTemplateTypes(templateTypesData.data || []);
        setSectionTypes(sectionTypesData.data || []);
        setDefaultTemplateRules(defaultTemplateRulesData);
        setDefaultSectionRules(defaultSectionRulesData);
      } catch (error) {
        console.error('Error loading data:', error);
        toast.error('Failed to load template data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const handleTemplateToggle = (templateType: TemplateType, enabled: boolean) => {
    const key = `${templateType}:post` as const;
    setTemplateRules(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        template_type: templateType,
        content_type: 'post',
        allowed_length_tags: enabled ? ['ultra-short', 'short', 'medium', 'medium-long', 'long', 'very-long'] : [],
        full_html_example: defaultTemplateRules[key]?.full_html_example || ''
      }
    }));
    setHasChanges(true);
  };

  const handleSectionToggle = (templateType: TemplateType, sectionId: string, enabled: boolean) => {
    const key = `${templateType}:post` as const;
    setSectionRules(prev => ({
      ...prev,
      [key]: (prev[key] || []).map(section => 
        section.section_id === sectionId
          ? { ...section, enabled }
          : section
      )
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await Promise.all(
        templateTypes.map(templateType => {
          const key = `${templateType.id}:post` as const;
          return TemplateRulesService.updateTemplateRules(
            websiteId,
            templateType.id as TemplateType,
            'post',
            templateRules[key]!,
            sectionRules[key] || []
          );
        })
      );

      setHasChanges(false);
      toast.success('Template rules saved successfully');
      onSave?.();
    } catch (error) {
      console.error('Error saving template rules:', error);
      toast.error('Failed to save template rules');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    try {
      setSaving(true);
      await TemplateRulesService.resetToDefaults(websiteId);
      setTemplateRules({});
      setSectionRules({});
      setHasChanges(false);
      toast.success('Template rules reset to defaults');
      onSave?.();
    } catch (error) {
      console.error('Error resetting template rules:', error);
      toast.error('Failed to reset template rules');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Alert>
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          <strong>Safety Notice:</strong> Image, Quote, and Testimonial sections are disabled by default to prevent empty or fake content. 
          These sections will only be used if you have properly configured them with real content. 
          You can enable them below, but ensure you have actual images, quotes, or testimonials to use.
        </AlertDescription>
      </Alert>
      
      <div className="flex justify-between items-center">
        <div className="space-y-1">
          <h2 className="text-lg font-semibold">Template Rules</h2>
          <p className="text-sm text-muted-foreground">
            Configure which templates and sections are available for your content.
            <Link to="/section-rules" className="text-primary hover:underline ml-1">
              Manage section content →
            </Link>
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleReset}
            disabled={saving || !hasChanges}
          >
            Reset to Defaults
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !hasChanges}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      <div className="space-y-4">
        {templateTypes.map((templateType) => {
          const key = `${templateType.id}:post` as const;
          const templateRule = templateRules[key] || defaultTemplateRules[key];
          const sectionRule = sectionRules[key] || defaultSectionRules[key] || [];

          return (
            <Card key={key}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium flex items-center">
                  {getTemplateIcon(templateType.icon)}
                  {templateType.name}
                </CardTitle>
                <Switch
                  checked={templateRule?.allowed_length_tags?.length > 0}
                  onCheckedChange={(checked) => handleTemplateToggle(templateType.id as TemplateType, checked)}
                />
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {templateRule?.allowed_length_tags?.length > 0 && (
                    <>
                      <div className="space-y-2">
                        <h4 className="text-sm font-medium">Available Sections</h4>
                        <p className="text-sm text-muted-foreground">
                          Enable or disable sections for this template type
                        </p>
                      </div>
                      <div className="space-y-4">
                        {sectionRule.map((section) => {
                          const sectionType = sectionTypes.find(st => st.id === section.section_id);
                          const isProblematicSection = ['image', 'quote', 'testimonial'].includes(section.section_id);
                          
                          return (
                            <div key={section.section_id} className="space-y-2">
                              <div className="flex items-center justify-between">
                                <div className="space-y-1">
                                  <div className="flex items-center gap-2">
                                    <span className={`text-sm font-medium ${isProblematicSection ? 'text-orange-600' : ''}`}>
                                      {sectionType?.name || section.section_id}
                                      {isProblematicSection && (
                                        <span className="ml-1 text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded">
                                          Requires real content
                                        </span>
                                      )}
                                    </span>
                                    <Switch
                                      checked={section.enabled}
                                      onCheckedChange={(checked) => handleSectionToggle(templateType.id as TemplateType, section.section_id, checked)}
                                    />
                                  </div>
                                  {sectionType?.description && (
                                    <p className="text-sm text-muted-foreground">
                                      {sectionType.description}
                                    </p>
                                  )}
                                  {isProblematicSection && (
                                    <p className="text-xs text-orange-600">
                                      ⚠️ This section requires real content to be properly configured before use
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}; 